import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { db, auth } from '../firebase';
import { updateCurrentUser, ROLES } from '../services/authService';
import './SettingsModule.css';

const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.SUPERVISOR]: 'Supervisor',
  [ROLES.TECHNICIAN]: 'Technician'
};

/**
 * Account Settings — name, password, notification preferences.
 * Self-contained styling (`.stg-` prefixed classes) so it drops cleanly
 * into either the technician Dashboard's card layout or the Dionysus-style
 * Supervisor Dashboard's `dio-content` area without fighting either theme.
 *
 * Props:
 *  - user:         current user object ({ id, name, email, role, notificationPrefs? })
 *  - onBack:       go back to the previous screen
 *  - onUserUpdate: optional — called with the merged, updated user object
 *                  whenever a save succeeds, so the parent can refresh its
 *                  own `user` state (App.js owns it; without this the new
 *                  name/prefs wouldn't show anywhere else until reload)
 */
function SettingsModule({ user, onBack, onUserUpdate }) {
  const [name, setName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState(null); // { type: 'success'|'error', text }

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const [notifPrefs, setNotifPrefs] = useState({
    newAssignments: user?.notificationPrefs?.newAssignments !== false,
    systemAlerts: user?.notificationPrefs?.systemAlerts !== false
  });
  const [savingNotifs, setSavingNotifs] = useState(false);
  const [notifMsg, setNotifMsg] = useState(null);

  const applyUserUpdate = (patch) => {
    const merged = { ...user, ...patch };
    updateCurrentUser(merged);
    if (onUserUpdate) onUserUpdate(merged);
  };

  // ── Name ────────────────────────────────────────────────────────────────
  const handleSaveName = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setNameMsg({ type: 'error', text: 'Name cannot be empty.' }); return; }
    setSavingName(true);
    setNameMsg(null);
    try {
      await updateDoc(doc(db, 'users', user.id), { name: trimmed });
      applyUserUpdate({ name: trimmed });
      setNameMsg({ type: 'success', text: 'Name updated.' });
    } catch (err) {
      console.error('Error updating name:', err);
      setNameMsg({ type: 'error', text: 'Could not save your name. Please try again.' });
    } finally {
      setSavingName(false);
    }
  };

  // ── Password ────────────────────────────────────────────────────────────
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'Please fill in all three password fields.' });
      return;
    }
    if (newPassword.length < 6) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New password and confirmation don\u2019t match.' });
      return;
    }

    setSavingPassword(true);
    try {
      const firebaseUser = auth.currentUser;
      // Firebase requires a recent login for sensitive ops like a password
      // change — re-authenticate with the current password first.
      const credential = EmailAuthProvider.credential(firebaseUser.email, currentPassword);
      await reauthenticateWithCredential(firebaseUser, credential);
      await updatePassword(firebaseUser, newPassword);

      setPasswordMsg({ type: 'success', text: 'Password updated.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error changing password:', err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPasswordMsg({ type: 'error', text: 'Current password is incorrect.' });
      } else if (err.code === 'auth/too-many-requests') {
        setPasswordMsg({ type: 'error', text: 'Too many attempts. Please wait a bit and try again.' });
      } else {
        setPasswordMsg({ type: 'error', text: 'Could not change your password. Please try again.' });
      }
    } finally {
      setSavingPassword(false);
    }
  };

  // ── Notification preferences ───────────────────────────────────────────
  const toggleNotifPref = (key) => {
    setNotifPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    setNotifMsg(null);
  };

  const handleSaveNotifs = async () => {
    setSavingNotifs(true);
    setNotifMsg(null);
    try {
      await updateDoc(doc(db, 'users', user.id), { notificationPrefs: notifPrefs });
      applyUserUpdate({ notificationPrefs: notifPrefs });
      setNotifMsg({ type: 'success', text: 'Notification preferences saved.' });
    } catch (err) {
      console.error('Error saving notification preferences:', err);
      setNotifMsg({ type: 'error', text: 'Could not save preferences. Please try again.' });
    } finally {
      setSavingNotifs(false);
    }
  };

  return (
    <div className="stg-wrap">
      <div className="stg-header">
        {onBack && <button className="stg-back-btn" onClick={onBack}>← Back</button>}
        <h1>Settings</h1>
      </div>

      {/* ── Account ── */}
      <div className="stg-card">
        <h2 className="stg-card-title">Account</h2>

        <form onSubmit={handleSaveName} className="stg-form">
          <label className="stg-label" htmlFor="stg-name">Full name</label>
          <input
            id="stg-name"
            type="text"
            className="stg-input"
            value={name}
            onChange={(e) => { setName(e.target.value); setNameMsg(null); }}
          />

          <label className="stg-label">Email</label>
          <input type="text" className="stg-input stg-input-readonly" value={user?.email || ''} readOnly />

          <label className="stg-label">Role</label>
          <div className="stg-role-badge">{ROLE_LABELS[user?.role] || user?.role || 'Unknown'}</div>

          {nameMsg && <div className={`stg-msg stg-msg-${nameMsg.type}`}>{nameMsg.text}</div>}

          <button type="submit" className="stg-btn stg-btn-primary" disabled={savingName || name.trim() === user?.name}>
            {savingName ? 'Saving...' : 'Save name'}
          </button>
        </form>
      </div>

      {/* ── Password ── */}
      <div className="stg-card">
        <h2 className="stg-card-title">Change password</h2>
        <form onSubmit={handleChangePassword} className="stg-form">
          <label className="stg-label" htmlFor="stg-current-pw">Current password</label>
          <input
            id="stg-current-pw"
            type="password"
            className="stg-input"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => { setCurrentPassword(e.target.value); setPasswordMsg(null); }}
          />

          <label className="stg-label" htmlFor="stg-new-pw">New password</label>
          <input
            id="stg-new-pw"
            type="password"
            className="stg-input"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setPasswordMsg(null); }}
          />

          <label className="stg-label" htmlFor="stg-confirm-pw">Confirm new password</label>
          <input
            id="stg-confirm-pw"
            type="password"
            className="stg-input"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setPasswordMsg(null); }}
          />

          {passwordMsg && <div className={`stg-msg stg-msg-${passwordMsg.type}`}>{passwordMsg.text}</div>}

          <button type="submit" className="stg-btn stg-btn-primary" disabled={savingPassword}>
            {savingPassword ? 'Updating...' : 'Update password'}
          </button>
        </form>
      </div>

      {/* ── Notifications ── */}
      <div className="stg-card">
        <h2 className="stg-card-title">Notifications</h2>

        <div className="stg-toggle-row">
          <div>
            <div className="stg-toggle-label">New assignment alerts</div>
            <div className="stg-toggle-sub">Get notified when work is assigned to you</div>
          </div>
          <button
            className={`stg-switch ${notifPrefs.newAssignments ? 'on' : ''}`}
            role="switch"
            aria-checked={notifPrefs.newAssignments}
            onClick={() => toggleNotifPref('newAssignments')}
          >
            <span className="stg-switch-knob" />
          </button>
        </div>

        <div className="stg-toggle-row">
          <div>
            <div className="stg-toggle-label">System alerts</div>
            <div className="stg-toggle-sub">Flagged photos, transfers, and other system notices</div>
          </div>
          <button
            className={`stg-switch ${notifPrefs.systemAlerts ? 'on' : ''}`}
            role="switch"
            aria-checked={notifPrefs.systemAlerts}
            onClick={() => toggleNotifPref('systemAlerts')}
          >
            <span className="stg-switch-knob" />
          </button>
        </div>

        {notifMsg && <div className={`stg-msg stg-msg-${notifMsg.type}`}>{notifMsg.text}</div>}

        <button className="stg-btn stg-btn-primary" onClick={handleSaveNotifs} disabled={savingNotifs}>
          {savingNotifs ? 'Saving...' : 'Save preferences'}
        </button>
      </div>
    </div>
  );
}

export default SettingsModule;
